import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import apiClient from '../../api/axiosConfig';
import SkeletonLoader from '../../components/SkeletonLoader';
import Pagination from '../../components/Pagination';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import { faSearch, faCheckCircle, faTimesCircle, faPlus, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_ENDPOINTS, ROUTES } from '../../constants';
import { useLocation, useNavigate } from 'react-router-dom';

function NewsletterSubscribers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [lastPage, setLastPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationModalAction, setConfirmationModalAction] = useState(null); // 'subscribe' or 'unsubscribe'
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newSubscriberEmail, setNewSubscriberEmail] = useState('');
  const [isAddingSubscriber, setIsAddingSubscriber] = useState(false);
  const [emailError, setEmailError] = useState(''); // New state for email validation error

  const { addToast } = useToast();

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchSubscribers = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(API_ENDPOINTS.NEWSLETTER_SUBSCRIBERS, {
        params: { page, per_page: perPage, search }
      });

      if (response.data.success) {
        const apiData = response.data;
        if (apiData && apiData.data && Array.isArray(apiData.data)) {
          setSubscribers(apiData.data);
          setLastPage(apiData.pagination?.last_page || 1);
        } else if (Array.isArray(apiData)) {
          setSubscribers(apiData);
          setLastPage(1);
        } else {
          console.error("API response for subscribers has unexpected data structure:", response.data);
          setSubscribers([]);
          setLastPage(1);
        }
      } else {
        addToast(response.data.message || 'Failed to fetch subscribers.', 'error');
        setError(response.data.message || 'Failed to fetch subscribers.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Error fetching subscribers.';
      addToast(errorMessage, 'error');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchSubscribers(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchSubscribers, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handlePageChange = (page) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', page);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleActionClick = (subscriber, action) => {
    setSelectedSubscriber(subscriber);
    setConfirmationModalAction(action);
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmAction = async () => {
    setIsConfirmationModalOpen(false);
    if (!selectedSubscriber || !confirmationModalAction) return;

    setLoading(true);
    setError(null);
    try {
      let response;
      if (confirmationModalAction === 'subscribe') {
        response = await apiClient.patch(API_ENDPOINTS.NEWSLETTER_SUBSCRIBERS_UPDATE(selectedSubscriber.id), { is_subscribed: true });
      } else if (confirmationModalAction === 'unsubscribe') {
        response = await apiClient.patch(API_ENDPOINTS.NEWSLETTER_SUBSCRIBERS_UPDATE(selectedSubscriber.id), { is_subscribed: false });
      }

      if (response.data.success) {
        addToast(response.data.message, 'success');
        await fetchSubscribers(currentPage, itemsPerPage, debouncedSearchTerm);
      } else {
        addToast(response.data.message || `Failed to ${confirmationModalAction} subscriber.`, 'error');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || `Error ${confirmationModalAction}ing subscriber.`;
      addToast(errorMessage, 'error');
      setError(errorMessage);
    } finally {
      setLoading(false);
      setSelectedSubscriber(null);
      setConfirmationModalAction(null);
    }
  };

  const handleCancelAction = () => {
    setIsConfirmationModalOpen(false);
    setSelectedSubscriber(null);
    setConfirmationModalAction(null);
  };

  const validateEmail = (email) => {
    if (!email) {
      return 'Email address is required.';
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Email address is invalid.';
    }
    return '';
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    setEmailError(''); // Clear previous error

    const validationMessage = validateEmail(newSubscriberEmail);
    if (validationMessage) {
      setEmailError(validationMessage);
      return;
    }

    setIsAddingSubscriber(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.NEWSLETTER_SUBSCRIBERS_STORE, { email: newSubscriberEmail });
      if (response.data.success) {
        addToast(response.data.message, 'success');
        setNewSubscriberEmail('');
        setIsAddModalOpen(false);
        await fetchSubscribers(currentPage, itemsPerPage, debouncedSearchTerm);
      } else {
        // Handle backend validation errors specifically
        if (response.data.errors && response.data.errors.email) {
          setEmailError(response.data.errors.email[0]);
          addToast(response.data.errors.email[0], 'error');
        } else {
          addToast(response.data.message || 'Failed to add subscriber.', 'error');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Error adding subscriber.';
      addToast(errorMessage, 'error');
      setEmailError(errorMessage); // Display generic error if no specific email error from backend
    } finally {
      setIsAddingSubscriber(false);
    }
  };

  const handleViewAnalytics = () => {
    navigate(ROUTES.NEWSLETTER_ANALYTICS);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Newsletter Subscribers</h1>
        <div className="flex space-x-4">
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-accent-red hover:bg-red-700 text-white"
            icon={faPlus}
          >
            Add New Subscriber
          </Button>
          <Button
            onClick={handleViewAnalytics}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            icon={faChartBar}
          >
            Analytics
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Subscribers"
          id="search-subscribers"
          name="search-subscribers"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by email..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Email</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Status</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Last Action At</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.length > 0 ? (
                  subscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{subscriber.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{subscriber.email}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {subscriber.unsubscribe_token !== null ? (
                          <span className="text-green-500">
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />Subscribed
                          </span>
                        ) : (
                          <span className="text-red-500">
                            <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />Unsubscribed
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {new Date(subscriber.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        {subscriber.unsubscribe_token !== null ? (
                          <Button
                            onClick={() => handleActionClick(subscriber, 'unsubscribe')}
                            className="text-red-600 hover:text-red-700 mr-3"
                            icon={faTimesCircle}
                            tooltipId={`unsubscribe-subscriber-tooltip-${subscriber.id}`}
                            tooltipContent="Unsubscribe"
                          />
                        ) : (
                          <Button
                            onClick={() => handleActionClick(subscriber, 'subscribe')}
                            className="text-green-500 hover:text-green-600 mr-3"
                            icon={faCheckCircle}
                            tooltipId={`subscribe-subscriber-tooltip-${subscriber.id}`}
                            tooltipContent="Subscribe"
                          />
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No newsletter subscribers found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {lastPage > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={lastPage}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmAction}
        title={`${confirmationModalAction === 'subscribe' ? 'Confirm Subscribe' : 'Confirm Unsubscribe'}`}
        message={`Are you sure you want to ${confirmationModalAction} ${selectedSubscriber?.email}?`}
      />

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle w-full max-w-md">
            <h2 className="text-xl font-bold text-text-white mb-4">Add New Subscriber</h2>
            <form onSubmit={handleAddSubscriber}>
              <TextInput
                label="Email Address"
                id="new-subscriber-email"
                name="new-subscriber-email"
                type="email"
                value={newSubscriberEmail}
                onChange={(e) => {
                  setNewSubscriberEmail(e.target.value);
                  setEmailError('');
                }}
                placeholder="Email Address"
                error={emailError}
              />
              <div className="flex justify-end space-x-4 mt-6">
                <Button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setNewSubscriberEmail('');
                    setEmailError('');
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-accent-red hover:bg-red-700 text-white"
                  disabled={isAddingSubscriber}
                >
                  {isAddingSubscriber ? 'Adding...' : 'Add Subscriber'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsletterSubscribers;